/* eslint-disable jsx-a11y/no-static-element-interactions */
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Backdrop,
  CircularProgress,
  FormHelperText,
  Grid,
  IconButton,
  Snackbar,
  Tooltip
} from '@mui/material';

import {
  CustomSelect,
  MDTypography,
  MDInput,
  MDBox,
  MDButton,
  FaIcon,
  ProjectDetailsModal,
  If,
  FileUploadModal,
  FullPageLoader,
  InsightsModal,
  CleanActionsModal
} from '@components';
import MainLayout from '@layouts/MainLayout';
import ReportGenerationModal from '@pages/ProjectBoard/components/ReportGenerationModal';
import RefreshDataModal from '@pages/ProjectBoard/components/RefreshDataModal';

import { RootState } from '@store/store';
import { useGetChartDetailsQuery, useNewChartMutation } from '@api/chart';
import {
  useGetAutoChartsMutation,
  useGetProjectByIdQuery,
  useLazyGetProjectByIdQuery,
  useRefreshSpreadsheetDataMutation,
  useSendSquirrelBotRequestMutation,
  useUpdateProjectMutation
} from '@api/projects';
import { useLazyGetFeatureAccessQuery } from '@api/usePlans';
import { resetConfigs, setCurrentProjectData } from '@pages/ProjectBoard/slice';

import { containsWhiteSpace, getFileType, getUTCTime } from '@utils/common';
import { sendAmplitudeData } from '@utils/amplitude';
import UserAuth from '@services/auth';
import useProjects from '@hooks/useProject';
import useToast from '@hooks/useToast';
import useCustomNavigate from '@hooks/useCustomNavigate';
import useUserDetails from '@hooks/useUserDetails';
import { ProjectDetails } from '@DStypes/project';
import colors from '@theme/base/colors';
import linearGradient from '@theme/functions/linearGradient';
import {
  CLICKED_ADD_DATA,
  CLICKED_AUTO_AI,
  CLICKED_DOWNLOAD,
  CLICKED_KNOW_MORE,
  CLICKED_SHOW_INSIGHT,
  CLICKED_SQBOT_DOWNVOTE,
  CLICKED_SQBOT_UPVOTE,
  CLICKED_VIEW_CLEANED_DATA,
  CONFIRM_REFRESH_DATA,
  INPUT_SQUIRREL_BOT,
  REMOVED_VOTE,
  USED_SQUIRBY
} from '@constants/eventNames';
import { FeatureNames, FeatureTypes, ProjectStatus } from '@constants/common';
import {
  ADD_CHART_PLACEHOLDER,
  ADD_CHART_TOOLTIP,
  CHART_COMBINATION_ERROR,
  CHART_CREATION,
  CHART_SUCCESSFULLY_CREATED,
  CUSTOMISE_CHART,
  ERROR,
  ERROR_STATUS_500,
  GO_AUTO,
  GO_AUTO_DESCRIPTION,
  GSHEET_UPDATE_INITIATED,
  INSIGHT_CREATION_FAILED,
  INVALID_BOT_INPUT,
  KPI_SUCCESSFULLY_CREATED,
  NO_DATA_MODIFICATIONS_AFTER_REFRESH
} from '@constants/messages';

import brandLogo from '@assets/images/DS_Logo.png';

interface Props {
  isGoAutoClicked?: (arg: boolean) => void;
  isLoading?: boolean;
  isProjectMode?: boolean;
}

const textStyles = {
  textAlign: 'center',
  variant: 'sm',
  color: 'info'
};

interface AlertProps {
  type:
    | 'chart-created'
    | 'chart-creation-failed'
    | 'bot-req-failed'
    | 'bot-req-success'
    | '';
  message: string;
  isKpi: boolean;
}

const AddChartNew: FC<Props> = ({
  isGoAutoClicked = () => null,
  isLoading = false,
  isProjectMode = false
}) => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const navigate = useCustomNavigate();
  const { pushInfoToast, pushErrorToast } = useToast();

  const subscriptionFlag = useSelector(
    (state: RootState) => state?.AppData?.subKeyFlag
  );
  const combineAppendFlag = useSelector(
    (state: RootState) => state?.AppData?.combineKeyFlag
  );
  const isCombineDataEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.combineAppendFile
  );
  const isUpdateDataEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.manualUpdate
  );
  const isSquirbyEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.squirby
  );
  const isAutoAiEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.autoAi
  );
  const isProjectInsightsEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.projectInsights
  );
  const projectBoard = useSelector((state: RootState) => state?.projectBoard);
  const userDetails = useSelector((state: RootState) => state?.userDetails);
  const user = UserAuth.userInfo;

  const { getUserDetails } = useUserDetails();
  const [
    refreshData,
    {
      data: dataRefreshResponse,
      isSuccess: isDataRefreshSuccess,
      isError: isDataRefreshError,
      error: dataRefreshError
    }
  ] = useRefreshSpreadsheetDataMutation();
  const { data, refetch } = useGetChartDetailsQuery({
    id: projectId as string
  });
  const [getAutoCharts, { isSuccess, isLoading: isGoAutoLoading }] =
    useGetAutoChartsMutation();
  const [addNewChart, { isSuccess: isChartCreationSuccess, error }] =
    useNewChartMutation();
  const [updateProject] = useUpdateProjectMutation();
  const [
    sendSquirrelBotRequest,
    {
      data: botRequestData,
      isSuccess: botReqSuccess,
      error: botReqError,
      isLoading: botReqLoading
    }
  ] = useSendSquirrelBotRequestMutation();
  const [sendVoteValue] = useSendSquirrelBotRequestMutation();
  const [getFeatureAccess] = useLazyGetFeatureAccessQuery();

  const showAddData = combineAppendFlag && projectBoard?.currentProjectData;
  const [cleanedColumnsRedirect, setCleanedColumnsRedirect] = useState(false);
  const [openDownloadModal, setOpenDownloadModal] = useState<boolean>(false);
  const [botSearchText, setBotSearchText] = useState<string>('');
  const [firstColumn, setFirstColumn] = useState('');
  const [secondColumn, setSecondColumn] = useState('');
  const [showGoAuto, setShowGoAuto] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [openInsightsModal, setOpenInsightsModal] = useState(false);
  const [startPollingForSquirby, setStartPollingForSquirby] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(
    isProjectMode ? 500 : 0
  );
  const [pollingIntForNewChart, setpollingIntForNewChart] = useState(500);
  const [showEditIcon, setShowEditIcon] = useState(false);
  const [isProjectDetailsModalOpen, setIsProjectDetailsMOdalOpen] =
    useState(false);
  const [alert, setAlert] = useState<AlertProps>({
    type: '',
    message: '',
    isKpi: false
  });
  const [openRefreshDataModal, setOpenRefreshDataModal] =
    useState<boolean>(false);
  const [chartType, setChartType] = useState<'chart' | 'kpi' | ''>('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isAddDataModalOpen, setIsAddDataModalOpen] = useState<boolean>();
  const [isCombineReady, setIsCombineReady] = useState<boolean>(false);
  const [isInitiateCombinePolling, setInitiateCombinePolling] = useState(false);
  const [combinePollingInterval, setCombinePollingInterval] =
    useState<number>(0);
  const [chartBoardLoader, setChartBoardLoader] = useState(false);
  const [voteType, setVoteType] = useState<number>(0);
  const [infoSnackBarMessage, setInfoSnackBarMessage] = useState<
    string | ReactNode
  >('');
  const [usedButton, setUsedButton] = useState(false);
  const [openCleanActionsModal, setOpenCleanActionsModal] = useState(false);
  const [clickedSeeMore, setClickedSeeMore] = useState(false);
  const [finalText, setFinalText] = useState('');
  const promptRef = useRef<HTMLDivElement>();

  const showTooltip = useMemo(() => {
    if (
      promptRef?.current &&
      promptRef?.current?.getBoundingClientRect().height > 25
    )
      return true;

    return false;
  }, [promptRef?.current]);

  const [getProjectById] = useLazyGetProjectByIdQuery(
    combinePollingInterval
      ? {
          pollingInterval: combinePollingInterval,
          selectFromResult: (response) => {
            if (
              response?.data?.project_status === 'READY' &&
              combinePollingInterval &&
              refetch
            ) {
              setIsCombineReady(false);
              setIsAddDataModalOpen(false);
              setCombinePollingInterval(0);
              setChartBoardLoader(false);
              setInitiateCombinePolling(false);
              refetch();
            }

            return response;
          }
        }
      : {}
  );

  const { data: projectData } = useGetProjectByIdQuery(
    { projectId },
    {
      pollingInterval: isSuccess || isProjectMode ? pollingInterval : 0,
      skip: !isSuccess && !isProjectMode
    }
  );

  const handleNavigate = (data: any, charts: any[]) => {
    setpollingIntForNewChart(0);
    if (charts.length === 0 && !startPollingForSquirby)
      dispatch(resetConfigs());
    const chartCount = data.length;
    const chartId = data[chartCount - 1]?.id;

    if (startPollingForSquirby) {
      setStartPollingForSquirby(false);
      if (botReqSuccess && isProjectMode) window.location.reload();
      if (botReqSuccess && !isProjectMode)
        navigate(`/project/${projectId}/edit`);
    } else if (chartCount) {
      navigate(`/chart/${chartId}/edit`, {
        state: {
          fromAddChartFlag: true,
          isGoAutoEnabled: showGoAuto
        }
      });
    }
  };

  const { chartConfigs, getChartConfigs } = useProjects({
    pollingInterval:
      isChartCreationSuccess || startPollingForSquirby
        ? pollingIntForNewChart
        : 0,
    pollingCallBack: (data: any[], charts: any[]) => {
      data?.length > charts?.length ? handleNavigate(data, charts) : null;
    }
  });

  useEffect(() => {
    if (botRequestData?.action === 'refresh') {
      setStartPollingForSquirby(true);
      getChartConfigs({
        projectId: projectId as string,
        collaboration_id: ''
      });
    }
  }, [botRequestData?.action]);

  useEffect(() => {
    if (projectData?.auto_charts_executed && pollingInterval) {
      if (!isProjectMode) {
        getChartConfigs({
          projectId: projectId as string,
          collaboration_id: ''
        });
        setPollingInterval(0);
        navigate(`/project/${projectId}/edit`);
      }
      setShowLoader(false);
    }
  }, [projectData]);

  useEffect(() => {
    if (
      projectData &&
      [ProjectStatus.READY, ProjectStatus.ADDING_DATA].includes(
        projectData.project_status
      ) &&
      isProjectMode
    ) {
      setPollingInterval(0);
      setChartBoardLoader(false);
    }

    if (projectData) dispatch(setCurrentProjectData(projectData));
  }, [projectData]);

  useEffect(() => {
    if (projectId && !isProjectMode)
      getChartConfigs({ projectId: projectId, collaboration_id: '' });
  }, []);

  useEffect(() => {
    if (isDataRefreshSuccess) {
      if (dataRefreshResponse === NO_DATA_MODIFICATIONS_AFTER_REFRESH)
        pushInfoToast(NO_DATA_MODIFICATIONS_AFTER_REFRESH);
      if (dataRefreshResponse === GSHEET_UPDATE_INITIATED)
        pushInfoToast(
          <>
            <span>Your data is being updated. Once complete,</span>
            <br />
            <span>1. A clean check will be run on your data again</span>
            <br />
            <span>
              2. Existing charts on your Dashboard will be updated with new data
            </span>
            <div>You will be notified once it is complete!</div>
          </>,
          { position: 'top-center' }
        );
    } else if (isDataRefreshError) {
      pushErrorToast(dataRefreshError.data);
    }
  }, [isDataRefreshSuccess, isDataRefreshError]);

  useEffect(() => {
    const autoCreatedCharts = chartConfigs?.filter(
      (chartConfig: any) => chartConfig.created_by_auto_charts === true
    );

    if (autoCreatedCharts?.length === 0) setShowGoAuto(true);
    else setShowGoAuto(false);
  }, [chartConfigs]);

  useEffect(() => {
    if (isSuccess) setPollingInterval(500);
  }, [isSuccess]);

  useEffect(() => {
    if (error)
      if (error?.data?.non_field_errors)
        setAlert({
          type: 'chart-creation-failed',
          message: CHART_COMBINATION_ERROR,
          isKpi: chartType === 'kpi' ? true : false
        });
      else
        setAlert({
          type: 'chart-creation-failed',
          message: ERROR,
          isKpi: chartType === 'kpi' ? true : false
        });

    if (isChartCreationSuccess)
      setAlert({
        type: 'chart-created',
        message:
          chartType === 'kpi'
            ? KPI_SUCCESSFULLY_CREATED
            : CHART_SUCCESSFULLY_CREATED,
        isKpi: chartType === 'kpi' ? true : false
      });
  }, [error, isChartCreationSuccess]);

  useEffect(() => {
    if (botReqError) {
      setBotSearchText('');
      setAlert({
        type: 'bot-req-failed',
        message:
          botReqError?.originalStatus === 500
            ? ERROR_STATUS_500
            : botRequestData?.message || INVALID_BOT_INPUT,
        isKpi: chartType === 'kpi' ? true : false
      });
      sendAmplitudeData(USED_SQUIRBY, {
        Status: 'fail',
        response_code: botReqError?.status || botReqError?.originalStatus
      });
    }
    if (botReqSuccess) {
      setBotSearchText('');
      setAlert({
        type: 'bot-req-success',
        message: botRequestData?.message
          ? botRequestData?.message
          : botRequestData?.chart_id
          ? CHART_SUCCESSFULLY_CREATED
          : INVALID_BOT_INPUT,
        isKpi: chartType === 'kpi' ? true : false
      });

      sendAmplitudeData(USED_SQUIRBY, {
        Status: 'success',
        response_code: botRequestData?.response_code,
        chart_id: botRequestData?.chart_id,
        action: botRequestData?.action,
        ...(botRequestData?.suggested_chart &&
          botRequestData?.suggested_chart?.x_type && {
            'Default Data Type combination': `${botRequestData?.suggested_chart?.x_type} & ${botRequestData?.suggested_chart?.y_type}`
          })
      });
    }
  }, [botReqError, botRequestData, botReqSuccess]);

  useEffect(() => {
    if (projectData?.project_status === ProjectStatus.READY && data) refetch();
  }, [projectData?.project_status]);

  useEffect(() => {
    if (
      projectBoard?.currentProjectData &&
      projectBoard?.currentProjectData?.project_status ===
        ProjectStatus.ADDING_DATA
    ) {
      setIsAddDataModalOpen(true);
      setIsCombineReady(true);
    }
  }, [projectBoard?.currentProjectData]);

  // Hook to initiate polling of project if not yet started
  // and project is in COMBINING or CLEANING status
  useEffect(() => {
    if (
      isInitiateCombinePolling ||
      (combinePollingInterval === 0 &&
        projectBoard?.currentProjectData &&
        projectBoard?.currentProjectData?.id &&
        [ProjectStatus.COMBINING, ProjectStatus.CLEANING].includes(
          projectBoard?.currentProjectData?.project_status as ProjectStatus
        ))
    ) {
      getProjectById({
        projectId: projectBoard?.currentProjectData?.id
      });
      setCombinePollingInterval(500);
      setChartBoardLoader(true);
    }
  }, [
    projectBoard?.currentProjectData,
    combinePollingInterval,
    isInitiateCombinePolling
  ]);

  useEffect(() => {
    if (
      !chartBoardLoader &&
      !showLoader &&
      !isLoading &&
      (projectBoard?.currentProjectData?.project_status
        ? projectBoard?.currentProjectData?.project_status ===
          ProjectStatus.READY
        : true)
    )
      setChartType('chart');
    else setChartType('');
  }, [
    chartBoardLoader,
    showLoader,
    isLoading,
    projectBoard?.currentProjectData
  ]);

  useEffect(() => {
    const userId = user?.id;

    if (UserAuth.isAuthenticated && userId) getUserDetails(userId as string);
  }, [botReqError, botReqSuccess]);

  const handleButtonClick = (action: string) => {
    setUsedButton(true);
    // if (action === 'refresh')
    //   if (isProjectMode) window.location.reload();
    //   else navigate(`/project/${projectId}/edit`);
    if (action === 'redirect' && botRequestData?.chart_id)
      navigate(`/chart/${botRequestData?.chart_id}/edit`);
  };

  const handleSquirrelBotReq = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Enter' && botSearchText.length !== 0 && !botReqLoading) {
      setUsedButton(false);
      sendAmplitudeData(INPUT_SQUIRREL_BOT, {
        location_type: isProjectMode ? 'Project Board' : 'New Chart'
      });
      setFinalText(botSearchText);
      setClickedSeeMore(false);
      setVoteType(0);
      sendSquirrelBotRequest({
        project_id: projectId,
        prompt_input: botSearchText
      });
    }
  };

  const handleGoAutoClick = async () => {
    sendAmplitudeData(CLICKED_AUTO_AI);

    if (subscriptionFlag) {
      const response = await getFeatureAccess(FeatureTypes.AUTO_AI);

      if (response?.isSuccess && !isGoAutoLoading) {
        const autoRes = await getAutoCharts({
          id: projectId,
          isAiDriven: true
        });

        if (isProjectMode)
          if (!(autoRes as any)?.error) isGoAutoClicked(true);
          else
            pushInfoToast(
              'Failed to auto-generate charts. Please try again after some time.'
            );
        else setShowLoader(true);
      }
    }
  };

  const handleCloseProjectDetailsModal = () =>
    setIsProjectDetailsMOdalOpen(false);

  const onUpdateProjectDetails = (projectDetails: ProjectDetails) => {
    updateProject({ id: projectId, payload: projectDetails });
    setIsProjectDetailsMOdalOpen(false);
    dispatch(setCurrentProjectData(projectDetails));
  };

  const onClickDownloadOrCancel = () => {
    setOpenDownloadModal((value) => !value);
    if (cleanedColumnsRedirect) setCleanedColumnsRedirect(false);
  };

  const handleCleanedDataDownloadOrCancel = () => {
    setCleanedColumnsRedirect((prev) => !prev);
    onClickDownloadOrCancel();
  };

  const handleColumnSubmit = () => {
    if (chartType === 'chart' && !firstColumn) {
      setErrorMessage('Please select atleast one column');

      return;
    }
    if (chartType === 'kpi' && !firstColumn) {
      setErrorMessage('Please select a column');

      return;
    }

    if (chartType === 'chart')
      addNewChart({
        project_id: projectId,
        x_axis: firstColumn,
        y_axis: secondColumn ? secondColumn : firstColumn,
        is_kpi: false
      });

    if (chartType === 'kpi')
      addNewChart({
        project_id: projectId,
        x_axis: firstColumn,
        y_axis: firstColumn,
        is_kpi: true
      });

    getChartConfigs({ projectId: projectId as string, collaboration_id: '' });
  };

  const getBotErrorMessage = () => {
    if (botReqError?.originalStatus === 500) return ERROR_STATUS_500;
    else return INVALID_BOT_INPUT;
  };

  const handleVoteClick = (rating: number) => {
    const newRating = voteType === rating ? 0 : rating;

    if (newRating === 1)
      sendAmplitudeData(CLICKED_SQBOT_UPVOTE, {
        action: botRequestData?.action
      });
    else if (newRating === -1)
      sendAmplitudeData(CLICKED_SQBOT_DOWNVOTE, {
        action: botRequestData?.action
      });
    else
      sendAmplitudeData(REMOVED_VOTE, {
        Rating: rating == 1 ? 'upvote' : 'downvote',
        Type: 'SqB'
      });

    setVoteType(newRating);
    sendVoteValue({
      project_id: projectId,
      prompt_input: finalText,
      rating: rating,
      sqb_id: botRequestData?.sqb_id
    });
  };

  const handleDisable = () => {
    if (isProjectMode)
      return (
        projectBoard?.currentProjectData?.project_status !==
          ProjectStatus.READY || isLoading
      );

    return showLoader;
  };

  const disableFirstColumn = () => {
    if (isProjectMode)
      return (
        projectBoard?.currentProjectData?.project_status !==
          ProjectStatus.READY ||
        isLoading ||
        showLoader ||
        chartType === ''
      );
    else return showLoader || chartType === '';
  };

  const disableSecondColumn = () => {
    if (isProjectMode)
      return (
        projectBoard?.currentProjectData?.project_status !==
          ProjectStatus.READY ||
        isLoading ||
        showLoader ||
        !firstColumn ||
        chartType !== 'chart'
      );
    else return showLoader || !firstColumn || chartType !== 'chart';
  };

  const onCloseAlert = () => {
    setAlert({
      type: '',
      message: '',
      isKpi: false
    });
  };

  const getSelectColumnTooltip = (type?: string) => {
    if (projectData?.project_status !== ProjectStatus.READY && isProjectMode)
      return 'You may create Charts when the data is processed and cleaned';
    else if (chartType === '') return 'Select KPI or Chart first.';
    else if (type === 'second' && chartType === 'kpi')
      return 'KPI card can be created using only one column. Please select OK to continue.';
    else if (type === 'second' && chartType === 'chart')
      return 'If you wish to only select one column name, click OK.';
    else return '';
  };

  const handleRefreshData = async () => {
    if (subscriptionFlag) {
      // If feature not accessible by plan then return
      const feature = isUpdateDataEnabledByPlan
        ? FeatureNames.MAX_MONTHLY_DATA_UPDATE_LIMIT
        : FeatureTypes.MANUAL_UPDATE;
      const response = await getFeatureAccess(feature);

      if (response?.isSuccess) setOpenRefreshDataModal(true);
    }
  };

  const handleRefreshDataModalClose = () => {
    setOpenRefreshDataModal(false);
  };

  const handleRefreshClick = () => {
    sendAmplitudeData(CONFIRM_REFRESH_DATA);

    handleRefreshDataModalClose();

    refreshData({ project_id: projectId });
  };

  const handleSquriByLockedOnChange = async () => {
    if (subscriptionFlag) getFeatureAccess(FeatureTypes.SQUIRBY);
  };

  return (
    <>
      <MainLayout
        sectionTitle={isProjectMode ? 'DASHBOARD' : 'CHART'}
        pageTitle={isProjectMode ? '0 Charts' : ''}
        showBackButton={!isProjectMode}
        sectionTooltip={!isProjectMode ? ADD_CHART_TOOLTIP : ''}
        goBackDestination={-1 as any}
        showUploadLimitMessage
      >
        {/* ProjectStatus not READY and Upload modal CLOSED*/}
        {chartBoardLoader && !isAddDataModalOpen && (
          <div style={{ position: 'relative' }}>
            <Backdrop
              sx={{
                height: '100vh',
                width: '100%',
                position: 'absolute',
                background: 'rgba(0,0,0,0)',
                zIndex: isAddDataModalOpen ? 500 : 50
              }}
              open={true}
              onClick={() => null}
            >
              <FullPageLoader />
            </Backdrop>
          </div>
        )}
        {/* ProjectStatus not READY and Upload modal OPEN*/}
        {chartBoardLoader && isAddDataModalOpen && (
          <div style={{ position: 'relative' }}>
            <Backdrop
              sx={{
                height: '80vh',
                width: '90%',
                position: 'absolute',
                background: 'rgba(0,0,0,0)',
                zIndex: isAddDataModalOpen ? 500 : 50
              }}
              open={true}
              onClick={() => null}
            >
              <CircularProgress />
            </Backdrop>
          </div>
        )}
        <Grid container display='flex' alignItems='center'>
          {isProjectMode ? (
            <Grid container display='flex' flexDirection='column'>
              <Grid
                item
                pt={2}
                display='flex'
                alignItems='center'
                width={'100%'}
                justifyContent={'space-between'}
              >
                <Grid
                  container
                  item
                  display={'flex'}
                  xs={7}
                  onMouseOver={() => setShowEditIcon(true)}
                  onMouseLeave={() => setShowEditIcon(false)}
                >
                  <Grid item maxWidth='55vw'>
                    <Tooltip
                      title={
                        projectBoard.currentProjectData?.project_name &&
                        projectBoard.currentProjectData?.project_name?.length <=
                          50
                          ? ''
                          : projectBoard.currentProjectData?.project_name || ''
                      }
                      placement='top'
                    >
                      <MDBox display='flex' alignItems='center' columnGap={0.5}>
                        <MDTypography
                          variant='h3'
                          color='info'
                          textGradient
                          onClick={() => setIsProjectDetailsMOdalOpen(true)}
                          sx={{
                            cursor: 'pointer',
                            whiteSpace: 'pre-wrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            wordBreak: containsWhiteSpace(
                              projectBoard.currentProjectData?.project_name ||
                                ''
                            )
                              ? 'normal'
                              : 'break-all'
                          }}
                        >
                          {projectBoard.currentProjectData?.project_name || ''}

                          <FaIcon
                            icon='pencil'
                            typographyProps={{ variant: 'lg', color: 'dark' }}
                            onClick={() => setIsProjectDetailsMOdalOpen(true)}
                            style={{
                              opacity: showEditIcon ? 1 : 0,
                              marginLeft: '5px'
                            }}
                          />
                        </MDTypography>
                      </MDBox>
                    </Tooltip>
                  </Grid>
                </Grid>
                <Grid
                  container
                  item
                  xs={5}
                  display='flex'
                  justifyContent='flex-end'
                  flexWrap='nowrap'
                  alignItems='center'
                >
                  <Tooltip
                    title='Click to generate automated insights'
                    placement='top'
                  >
                    <span>
                      <IconButton
                        size='small'
                        onClick={async () => {
                          sendAmplitudeData(CLICKED_SHOW_INSIGHT, {
                            type: 'Project',
                            red_flag: 'No'
                          });
                          if (subscriptionFlag) {
                            // If feature not accessible by plan then return

                            const feature = isProjectInsightsEnabledByPlan
                              ? FeatureTypes.MAX_MONTHLY_INSIGHTS_LIMIT
                              : FeatureTypes.PROJECT_INSIGHTS;

                            const response = await getFeatureAccess(feature);

                            if (response?.isSuccess) setOpenInsightsModal(true);
                          }
                        }}
                        sx={{
                          mr: '3px',
                          opacity: handleDisable() ? 0.5 : 1
                        }}
                        disabled={handleDisable()}
                      >
                        <FaIcon
                          icon='lightbulb-on'
                          typographyProps={{ color: 'primary', variant: 'xl' }}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title='Download cleaned file' placement='top'>
                    <span>
                      <IconButton
                        onClick={() => {
                          sendAmplitudeData(CLICKED_DOWNLOAD);
                          onClickDownloadOrCancel();
                        }}
                        size='small'
                        sx={{
                          mr: '3px',
                          opacity: handleDisable() ? 0.5 : 1
                        }}
                        disabled={handleDisable()}
                      >
                        <FaIcon
                          icon='download'
                          typographyProps={{ color: 'dark', variant: 'xl' }}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title='View File information' placement='top'>
                    <span>
                      <IconButton
                        onClick={() => {
                          setOpenCleanActionsModal(true);
                          sendAmplitudeData(CLICKED_VIEW_CLEANED_DATA);
                        }}
                        sx={{ mr: '10px', opacity: handleDisable() ? 0.5 : 1 }}
                        disabled={handleDisable()}
                      >
                        <FaIcon
                          icon='table-layout'
                          typographyProps={{ color: 'dark', variant: 'xl' }}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {getFileType(
                    projectBoard?.currentProjectData?.project_datafiles || []
                  ) === 'GSHEET' && (
                    <Tooltip
                      title={
                        <>
                          <span>
                            Use this feature to automatically add new rows from{' '}
                            {projectBoard.currentProjectData?.project_name ||
                              ''}{' '}
                            and update all charts (if any) with the newly added
                            data. Note that only new rows will be imported.
                          </span>
                          <br />
                          <br />
                          <span>
                            If you want to combine data from another G-sheet or
                            a different sheet in same G-sheet, use “Combine
                            Data”
                          </span>
                        </>
                      }
                      placement='top'
                    >
                      <MDButton
                        size='small'
                        onClick={handleRefreshData}
                        color='light'
                        sx={{
                          mr: '10px',
                          p: 0.8
                        }}
                        disabled={handleDisable()}
                      >
                        {subscriptionFlag &&
                          (isUpdateDataEnabledByPlan ? (
                            <FaIcon
                              icon='lock-open'
                              style={{ marginRight: '7px' }}
                              typographyProps={{
                                variant: 'xs',
                                color: 'primary',
                                mt: '-1.5px'
                              }}
                            />
                          ) : (
                            <FaIcon
                              icon='lock'
                              style={{ marginRight: '7px' }}
                              typographyProps={{
                                variant: 'xs',
                                color: 'primary',
                                mt: '-1.5px'
                              }}
                            />
                          ))}
                        <MDTypography
                          textTransform='capitalize'
                          variant='sm'
                          color='info'
                          fontWeight='bold'
                        >
                          UPDATE DATA
                        </MDTypography>
                      </MDButton>
                    </Tooltip>
                  )}
                  {showAddData && (
                    <Tooltip
                      title={
                        <div style={{ textAlign: 'left' }}>
                          <div>Use this feature to:</div>
                          <ul
                            style={{
                              listStyleType: 'disc',
                              paddingLeft: '20px'
                            }}
                          >
                            <li>
                              Merge different data files, such as combining
                              sales data from January with February.
                            </li>
                            <li>
                              Combine columns from different files using a
                              common ID or name.
                            </li>
                            <li>
                              Link to a different tab within the same Google
                              Sheet (note: this option is available for Google
                              Sheet projects only).
                            </li>
                            <li>
                              Automatically update project data using &apos;
                              Connect Data.&apos;
                            </li>
                          </ul>
                          Any existing charts and downloadable data files will
                          automatically update with new data.{' '}
                          <a
                            href='https://www.notion.so/datasquirrel/What-does-Combine-Data-do-748d2f782e5c4cd99b64a5a66c67da3c?pvs=4'
                            target='_blank'
                            rel='noreferrer'
                            style={{
                              color: 'white',
                              textDecoration: 'underline',
                              textUnderlineOffset: 2
                            }}
                            onClick={() =>
                              sendAmplitudeData(CLICKED_KNOW_MORE, {
                                'Feature Name': 'Combine Data'
                              })
                            }
                          >
                            Click here to learn more.
                          </a>
                        </div>
                      }
                      placement='top'
                    >
                      <span>
                        <MDButton
                          size='small'
                          onClick={async () => {
                            sendAmplitudeData(CLICKED_ADD_DATA);
                            if (subscriptionFlag) {
                              // If feature not accessible by plan then return
                              const response = await getFeatureAccess(
                                FeatureTypes.COMBINE_APPEND_FILE
                              );

                              if (response?.isSuccess)
                                setIsAddDataModalOpen(true);
                            }
                          }}
                          color='light'
                          sx={{
                            mr: '10px',
                            p: 0.8
                          }}
                          disabled={handleDisable()}
                        >
                          {subscriptionFlag &&
                            (isCombineDataEnabledByPlan ? (
                              <FaIcon
                                icon='lock-open'
                                style={{ marginRight: '7px' }}
                                typographyProps={{
                                  variant: 'xs',
                                  color: 'primary',
                                  mt: '-1.5px'
                                }}
                              />
                            ) : (
                              <FaIcon
                                icon='lock'
                                style={{ marginRight: '7px' }}
                                typographyProps={{
                                  variant: 'xs',
                                  color: 'primary',
                                  mt: '-1.5px'
                                }}
                              />
                            ))}
                          <FaIcon
                            icon='plus-square'
                            typographyProps={{
                              color: 'primary',
                              variant: 'xl'
                            }}
                            style={{ paddingLeft: 1 }}
                          />
                          <MDTypography
                            textTransform='capitalize'
                            variant='sm'
                            color='info'
                            fontWeight='bold'
                            pl={1}
                          >
                            COMBINE DATA
                          </MDTypography>
                        </MDButton>
                      </span>
                    </Tooltip>
                  )}
                </Grid>
              </Grid>
              <Grid item>
                <Tooltip
                  title={
                    projectBoard.currentProjectData?.project_short_desc || ''
                  }
                  placement='bottom-start'
                  onClick={() => {
                    setIsProjectDetailsMOdalOpen(true);
                  }}
                >
                  <MDTypography
                    width='fit-content'
                    variant='h5'
                    color='info'
                    sx={{
                      overflowWrap: 'anywhere',
                      whiteSpace: 'pre-wrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 5,
                      WebkitBoxOrient: 'vertical',
                      opacity: projectBoard.currentProjectData
                        ?.project_short_desc
                        ? 1
                        : 0.5,
                      cursor: 'pointer',
                      wordBreak: 'normal'
                    }}
                  >
                    {projectBoard.currentProjectData?.project_short_desc ||
                      'Add a description to the project (it will appear in downloaded project report)'}
                  </MDTypography>
                </Tooltip>
              </Grid>
            </Grid>
          ) : (
            <Grid item p={0.5} width={'100%'}>
              <MDBox
                display='flex'
                alignItems='center'
                columnGap={0.5}
                justifyContent={'space-between'}
              >
                <MDTypography variant='h3' color='info' textGradient>
                  New Chart
                </MDTypography>
                <MDBox display={{ xs: 'none', md: 'flex' }}>
                  <Tooltip title='View File information' placement='top'>
                    <IconButton
                      onClick={() => {
                        setOpenCleanActionsModal(true);
                        sendAmplitudeData(CLICKED_VIEW_CLEANED_DATA);
                      }}
                    >
                      <FaIcon
                        icon='table-layout'
                        typographyProps={{ color: 'dark', variant: 'xl' }}
                      />
                    </IconButton>
                  </Tooltip>
                </MDBox>
              </MDBox>
            </Grid>
          )}
          <Grid
            item
            display='flex'
            justifyContent='center'
            alignItems='center'
            alignContent='center'
            width='100%'
          >
            <Grid
              container
              direction='column'
              width='800px'
              justifyContent='center'
              minHeight='80vh'
            >
              {/* Section 1 */}

              <Grid item>
                <MDBox
                  position='relative'
                  display='flex'
                  flexDirection='column'
                  className='squirrel-bot-convo'
                >
                  <MDBox display='flex' flexDirection='row' mb={1} mt={3}>
                    {subscriptionFlag &&
                      (isSquirbyEnabledByPlan ? (
                        <FaIcon
                          icon='lock-open'
                          typographyProps={{
                            variant: 'xs',
                            color: 'primary',
                            mt: '8px',
                            mr: 0.5
                          }}
                        />
                      ) : (
                        <FaIcon
                          icon='lock'
                          typographyProps={{
                            variant: 'xs',
                            color: 'primary',
                            mt: '8px',
                            mr: 0.5
                          }}
                        />
                      ))}
                    <FaIcon
                      icon='sparkles'
                      size='2xs'
                      typographyProps={{ mt: '-8.5px' }}
                    />
                    <MDTypography
                      variant='h4'
                      color='dark'
                      mx='3px'
                      sx={{ fontWeight: '600' }}
                    >
                      Ask Squirby!
                    </MDTypography>
                    <Tooltip
                      title={
                        <div style={{ textAlign: 'left' }}>
                          <>
                            Squirby is an AI bot. Please check the answers once.
                          </>
                          <br />
                          <br />
                          <>
                            {' '}
                            DataSquirrel does not send raw data to an LLM for
                            security & data privacy reasons. We use LLM’s for
                            assisted analysis. However, it is recommended that
                            you verify any answers to ensure there are no
                            unexpected / untrue outcomes. In AI those are called
                            hallucinations.
                          </>
                        </div>
                      }
                      placement='top'
                    >
                      <span>
                        <FaIcon
                          icon='circle-info'
                          size='xs'
                          typographyProps={{
                            color: 'primary'
                          }}
                        />
                      </span>
                    </Tooltip>
                  </MDBox>
                  {
                    <If
                      condition={
                        botRequestData ||
                        (botReqError && botReqError?.status !== 402)
                      }
                    >
                      <Grid
                        container
                        display='flex'
                        flexDirection='column'
                        gap={2}
                        width='840px'
                        mb={1}
                      >
                        <Grid
                          item
                          width='fit-content'
                          alignSelf='flex-end'
                          sx={({ palette }) => ({
                            ml: '20%',
                            border: `1px solid ${palette.grey[300]}`,
                            backgroundColor: palette.light.main,
                            p: 1,
                            borderRadius: '5px',
                            boxShadow: '2px 2px 5px rgba(0, 0, 0, 0.1)',
                            maxWidth: '80%',
                            minWidth: '50%'
                          })}
                        >
                          <Grid
                            container
                            display='flex'
                            flexDirection='row'
                            alignItems='center'
                            justifyContent='flex-end'
                            flexWrap='nowrap'
                            alignSelf='flex-end'
                            gap={1}
                          >
                            <MDTypography variant='sm' color='dark'>
                              YOU:
                            </MDTypography>
                            <Tooltip title={showTooltip ? finalText : ''}>
                              <MDTypography
                                ref={promptRef}
                                variant='sm'
                                color='dark'
                                sx={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  wordBreak: containsWhiteSpace(finalText)
                                    ? 'normal'
                                    : 'break-all'
                                }}
                              >
                                {finalText}
                              </MDTypography>
                            </Tooltip>
                          </Grid>
                        </Grid>
                        <Grid item maxWidth='80%' minWidth='50%'>
                          <Grid
                            container
                            width='fit-content'
                            maxHeight='250px'
                            display='flex'
                            flexDirection='row'
                            alignItems='flex-start'
                            justifyContent='flex-start'
                            flexWrap='nowrap'
                            gap={0.5}
                            sx={({ palette }) => ({
                              border: `1px solid ${palette.primary.main}`,
                              borderRadius: '5px',
                              overflowY: 'auto',
                              px: 1,
                              backgroundColor: palette.light.main,
                              boxShadow: '2px 2px 5px rgba(0, 0, 0, 0.1)'
                            })}
                          >
                            <MDBox
                              component='img'
                              src={brandLogo}
                              alt='Brand'
                              sx={{
                                height: 50,
                                width: 50,
                                minWidth: 50,
                                padding: '10px'
                              }}
                            />
                            <Grid
                              container
                              display='flex'
                              flexDirection='column'
                              gap={1}
                            >
                              <MDTypography
                                variant='sm'
                                color='dark'
                                mt={1}
                                sx={{
                                  wordBreak: containsWhiteSpace(
                                    botRequestData?.message
                                  )
                                    ? 'normal'
                                    : 'break-all'
                                }}
                              >
                                {botReqError ? (
                                  getBotErrorMessage()
                                ) : clickedSeeMore ? (
                                  botRequestData?.message
                                ) : (
                                  <>
                                    {' '}
                                    {botRequestData?.message?.length > 155
                                      ? botRequestData?.message.slice(0, 155) +
                                        '...'
                                      : botRequestData?.message}
                                    <span
                                      style={{
                                        textDecoration: 'underline',
                                        textUnderlineOffset: '3px',
                                        cursor: 'pointer'
                                      }}
                                      onClick={() => setClickedSeeMore(true)}
                                    >
                                      {botRequestData?.message?.length > 155
                                        ? 'read more'
                                        : ''}
                                    </span>
                                  </>
                                )}
                              </MDTypography>
                              <Grid
                                display='flex'
                                justifyContent='flex-end'
                                gap={1}
                                mb={1}
                              >
                                {botRequestData?.sqb_id && (
                                  <Grid
                                    container
                                    display='flex'
                                    flexDirection='row'
                                    gap={1}
                                    alignItems='center'
                                    width='fit-content'
                                  >
                                    <Tooltip title='Select if SquirrelBot response was helpful!'>
                                      <span>
                                        <FaIcon
                                          icon={[
                                            voteType === 1 ? 'fas' : 'far',
                                            'thumbs-up'
                                          ]}
                                          style={{
                                            cursor: 'pointer'
                                          }}
                                          onClick={() => handleVoteClick(1)}
                                        />
                                      </span>
                                    </Tooltip>
                                    <Tooltip title='Select if SquirrelBot response was not satisfactory :('>
                                      <span>
                                        <FaIcon
                                          icon={[
                                            voteType === -1 ? 'fas' : 'far',
                                            'thumbs-down'
                                          ]}
                                          style={{
                                            cursor: 'pointer'
                                          }}
                                          onClick={() => handleVoteClick(-1)}
                                        />
                                      </span>
                                    </Tooltip>
                                  </Grid>
                                )}
                                {!botReqError &&
                                  botRequestData?.chart_id &&
                                  !usedButton && (
                                    <MDTypography
                                      variant='h5'
                                      color='dark'
                                      underline
                                      sx={{
                                        textUnderlineOffset: 3,
                                        cursor: 'pointer'
                                      }}
                                      onClick={() =>
                                        handleButtonClick('redirect')
                                      }
                                    >
                                      {botRequestData?.button_text ||
                                        'Take me there'}
                                    </MDTypography>
                                  )}
                                {!botReqError &&
                                  !botRequestData?.chart_id &&
                                  botRequestData?.button_text &&
                                  !usedButton && (
                                    <MDTypography
                                      variant='h5'
                                      color='dark'
                                      underline
                                      sx={{
                                        textUnderlineOffset: 3,
                                        cursor: 'pointer'
                                      }}
                                      onClick={() =>
                                        handleButtonClick(
                                          botRequestData?.action
                                        )
                                      }
                                    >
                                      {botRequestData?.button_text}
                                    </MDTypography>
                                  )}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </If>
                  }
                  <MDBox
                    display='flex'
                    justifyContent='flex-start'
                    // sx={{ height: '42px' }}
                  >
                    <MDBox
                      sx={({ palette }) => ({
                        background: linearGradient(
                          palette.gradients.secondary.main,
                          palette.gradients.secondary.state
                        ),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 50,
                        boxShadow: '0 3px 10px rgb(0 0 0 / 0.2)',
                        height: 38,
                        width: 38,
                        padding: '5px',
                        position: 'absolute',
                        left: -45,
                        bottom: 32
                      })}
                    >
                      <MDBox
                        component='img'
                        src={brandLogo}
                        alt='Brand'
                        sx={{
                          height: 45,
                          width: 45,
                          minWidth: 50,
                          padding: '12px'
                        }}
                      />
                    </MDBox>
                    <MDInput
                      placeholder={ADD_CHART_PLACEHOLDER}
                      needEnterIcon
                      onChange={
                        !isSquirbyEnabledByPlan
                          ? handleSquriByLockedOnChange
                          : (e: React.ChangeEvent<HTMLInputElement>) => {
                              if (!botReqLoading)
                                setBotSearchText(e.target.value);
                              if (alert.type === 'bot-req-failed')
                                onCloseAlert();
                            }
                      }
                      onKeyDown={handleSquirrelBotReq}
                      onClickEnter={() => {
                        if (botSearchText.length !== 0 && !botReqLoading) {
                          sendAmplitudeData(INPUT_SQUIRREL_BOT, {
                            location_type: isProjectMode
                              ? 'Project Board'
                              : 'New Chart'
                          });
                          setFinalText(botSearchText);
                          setClickedSeeMore(false);
                          setVoteType(0);
                          sendSquirrelBotRequest({
                            project_id: projectId,
                            prompt_input: botSearchText
                          });
                        }
                      }}
                      value={botSearchText}
                      sx={{
                        mt: botReqError || botReqSuccess ? 1 : 0,
                        marginBottom: 4,
                        width: '100%',
                        outerHeight: '42px',
                        '& .MuiOutlinedInput-root': {
                          '& > fieldset': {
                            borderColor: colors.secondary.main
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.secondary.main
                          },
                          '& .MuiTypography-root': {
                            marginTop: '2px'
                          }
                        }
                      }}
                      inputProps={{
                        sx: {
                          textAlign: 'initial',
                          fontSize: '16px',
                          height: '10px'
                        }
                      }}
                      disabled={handleDisable()}
                      color='secondary'
                      isLoading={botReqLoading}
                    />
                  </MDBox>
                </MDBox>
              </Grid>

              <MDTypography
                variant='h5'
                color='dark'
                textAlign='center'
                mb={3}
                fontWeight='bold'
              >
                - OR -
              </MDTypography>

              {/* Section 2 */}
              <Grid item>
                <MDBox display='flex' flexDirection='column'>
                  <MDTypography
                    variant='h4'
                    color='dark'
                    mb={2}
                    sx={{ fontWeight: '600' }}
                  >
                    Try out Auto suggestions
                  </MDTypography>
                  <MDBox
                    display='flex'
                    justifyContent='center'
                    width='100%'
                    mb={1.5}
                  >
                    <Tooltip
                      title={
                        !showGoAuto
                          ? 'Auto AI has already been used for this project'
                          : showLoader || isLoading
                          ? 'Processing request. Please wait…This may take upto a minute'
                          : projectData?.project_status !==
                              ProjectStatus.READY && isProjectMode
                          ? 'You may create Charts when the data is processed and cleaned'
                          : 'Select to create optimal charts using our AI engine'
                      }
                      placement='top'
                    >
                      <MDBox>
                        <MDButton
                          color='light'
                          size='medium'
                          onClick={() => handleGoAutoClick()}
                          disabled={
                            !showGoAuto || isGoAutoLoading || handleDisable()
                          }
                          sx={{ boxShadow: '2px 2px 5px rgba(0, 0, 0, 0.1)' }}
                        >
                          {subscriptionFlag &&
                            (isAutoAiEnabledByPlan ? (
                              <FaIcon
                                icon='lock-open'
                                style={{ marginRight: '7px' }}
                                typographyProps={{
                                  variant: 'xs',
                                  color: 'primary',
                                  mt: '-1.5px'
                                }}
                              />
                            ) : (
                              <FaIcon
                                icon='lock'
                                style={{ marginRight: '7px' }}
                                typographyProps={{
                                  variant: 'xs',
                                  color: 'primary',
                                  mt: '-1.5px'
                                }}
                              />
                            ))}
                          <FaIcon
                            typographyProps={{
                              variant: 'xs',
                              color: 'primary'
                            }}
                            icon='chart-simple'
                          />
                          <MDTypography
                            fontWeight='bold'
                            textTransform='capitalize'
                            variant='sm'
                            color='info'
                            pl={1}
                          >
                            AUTO AI
                          </MDTypography>
                          {(showLoader || isLoading) && (
                            <MDBox
                              sx={{
                                marginLeft: '10px',
                                alignSelf: 'center',
                                marginTop: '3px'
                              }}
                            >
                              <CircularProgress
                                size={20}
                                style={{ color: 'primary' }}
                              />
                            </MDBox>
                          )}
                        </MDButton>
                      </MDBox>
                    </Tooltip>
                  </MDBox>
                  <MDTypography {...textStyles}>{GO_AUTO}</MDTypography>
                  <MDTypography {...textStyles} mb={2}>
                    {GO_AUTO_DESCRIPTION}
                  </MDTypography>
                </MDBox>
                <MDTypography
                  variant='h5'
                  color='dark'
                  textAlign='center'
                  mb={3}
                  fontWeight='bold'
                >
                  - OR -
                </MDTypography>
              </Grid>

              {/* Section 3 */}
              <Grid item>
                <MDBox display='flex' flexDirection='column' mb={2}>
                  <MDTypography
                    variant='h4'
                    color='dark'
                    mb={1}
                    sx={{ fontWeight: '600' }}
                  >
                    Create your own chart
                  </MDTypography>
                  <MDBox display='flex' alignItems='flex-end' marginLeft={6.5}>
                    <MDBox marginX='25px'>
                      <MDBox
                        display='flex'
                        width='100%'
                        flexDirection='row'
                        flexWrap='nowrap'
                        justifyContent='center'
                        alignItems='center'
                        mb={2}
                        columnGap={2}
                      >
                        <Grid
                          item
                          display='flex'
                          flexDirection='row'
                          sx={{
                            width: '330px',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <MDTypography
                            variant='sm'
                            sx={{
                              fontWeight: 600,
                              opacity: chartType ? 1 : 0.5
                            }}
                            mt={2.5}
                          >
                            Step 1:{' '}
                            <span>
                              <MDTypography
                                variant='sm'
                                color='info'
                                sx={{ fontWeight: 400 }}
                              >
                                Select Type
                              </MDTypography>
                            </span>
                          </MDTypography>
                          <MDBox
                            width='50%'
                            display='flex'
                            justifyContent='flex-end'
                            alignItems='flex-end'
                            flexDirection='column'
                          >
                            <MDTypography variant='h6' mb={0.5}>
                              CHART
                            </MDTypography>
                            <MDButton
                              color='light'
                              sx={({ palette }) => ({
                                borderColor:
                                  chartType === 'chart'
                                    ? palette.secondary.main
                                    : '',
                                height: '80px',
                                width: '120px',
                                boxShadow: '2px 2px 5px rgba(0, 0, 0, 0.1)'
                              })}
                              onClick={() => {
                                setChartType('chart');
                                setErrorMessage('');
                              }}
                              size='large'
                              disabled={handleDisable()}
                            >
                              <FaIcon
                                typographyProps={{
                                  color: 'primary',
                                  variant: 'xl'
                                }}
                                icon='chart-simple'
                              />
                            </MDButton>
                          </MDBox>
                        </Grid>
                        <Grid
                          item
                          display='flex'
                          flexDirection='column'
                          width='330px'
                        >
                          <MDTypography
                            variant='h6'
                            mb={0.5}
                            sx={{ textAlign: 'end', width: '120px' }}
                          >
                            KPI
                          </MDTypography>
                          <MDButton
                            color='light'
                            sx={({ palette }) => ({
                              borderColor:
                                chartType === 'kpi'
                                  ? palette.secondary.main
                                  : '',
                              height: '80px',
                              position: 'relative',
                              width: '120px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              boxShadow: '2px 2px 5px rgba(0, 0, 0, 0.1)'
                            })}
                            onClick={() => {
                              setChartType('kpi');
                              setSecondColumn('');
                              setErrorMessage('');
                            }}
                            disabled={handleDisable()}
                          >
                            <FaIcon
                              icon={['far', 'gauge-high']}
                              typographyProps={{
                                color: 'primary',
                                variant: 'xl'
                              }}
                            />
                            <MDTypography
                              variant='h6'
                              color='info'
                              sx={{ position: 'absolute', bottom: 10 }}
                            >
                              240,000
                            </MDTypography>
                          </MDButton>
                        </Grid>
                      </MDBox>
                      <MDTypography
                        variant='sm'
                        color='dark'
                        mb={1}
                        sx={{
                          fontWeight: '600',
                          opacity: chartType ? 1 : 0.5
                        }}
                      >
                        Step 2:{' '}
                        <span>
                          <MDTypography
                            variant='sm'
                            color='info'
                            sx={{ fontWeight: 400 }}
                          >
                            Select Column (s)
                          </MDTypography>
                        </span>
                      </MDTypography>
                      <If
                        condition={
                          alert.type === 'chart-creation-failed' ||
                          alert.type === 'chart-created'
                        }
                      >
                        <Alert
                          severity={
                            alert.type === 'chart-created' ? 'success' : 'info'
                          }
                          sx={{
                            width: '100%',
                            maxWidth: '675px',
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2
                          }}
                          onClose={onCloseAlert}
                        >
                          {alert.message}
                        </Alert>
                      </If>
                      <MDBox
                        display='flex'
                        width='100%'
                        height='30px'
                        flexWrap='nowrap'
                        columnGap={2}
                      >
                        <CustomSelect
                          placeholder=' SELECT FIRST COLUMN'
                          options={data?.available_fields || []}
                          value={firstColumn}
                          handleValueChange={(e: any) => {
                            setErrorMessage('');
                            setFirstColumn(e.target.value);
                          }}
                          handleClearValue={() => setFirstColumn('')}
                          sx={{
                            width: '330px',
                            marginBottom: 2
                          }}
                          color='highlight'
                          isAddChart
                          disabled={disableFirstColumn()}
                        />
                        <CustomSelect
                          placeholder=' SELECT SECOND COLUMN (OPTIONAL)'
                          options={data?.available_fields || []}
                          value={secondColumn}
                          handleValueChange={(e: any) => {
                            setErrorMessage('');
                            setSecondColumn(e.target.value);
                          }}
                          handleClearValue={() => setSecondColumn('')}
                          disabled={disableSecondColumn()}
                          sx={{
                            width: '330px'
                          }}
                          color='highlight'
                          isAddChart
                          tooltipText={getSelectColumnTooltip('second')}
                        />
                      </MDBox>
                    </MDBox>
                    <MDButton
                      color='primary'
                      size='small'
                      variant='gradient'
                      onClick={() => handleColumnSubmit()}
                      disabled={
                        chartType === '' || handleDisable() || isGoAutoLoading
                      }
                    >
                      OK
                    </MDButton>
                  </MDBox>
                  {errorMessage && (
                    <FormHelperText
                      sx={{ mb: 1, ml: '25px', display: 'block', mt: 0.5 }}
                      error
                    >
                      {errorMessage}
                    </FormHelperText>
                  )}
                  <MDTypography sx={{ mt: 2 }} {...textStyles}>
                    {CHART_CREATION}
                  </MDTypography>
                  <MDTypography {...textStyles} mb={2}>
                    {CUSTOMISE_CHART}
                  </MDTypography>
                </MDBox>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MainLayout>
      {isProjectDetailsModalOpen && (
        <ProjectDetailsModal
          open={isProjectDetailsModalOpen}
          onClose={handleCloseProjectDetailsModal}
          onUpdateProjectDetails={onUpdateProjectDetails}
        />
      )}
      {openDownloadModal && (
        <ReportGenerationModal
          open={openDownloadModal}
          onClose={onClickDownloadOrCancel}
          disableDownloadReport
          themeData={[]}
          chartConfigs={[]}
          svgGeneratedCharts={[]}
          cleanedColumnsRedirect={cleanedColumnsRedirect}
        />
      )}
      {openCleanActionsModal && (
        <CleanActionsModal
          open={openCleanActionsModal}
          onClose={() => setOpenCleanActionsModal(false)}
          setDownloadClicked={handleCleanedDataDownloadOrCancel}
        />
      )}
      {isAddDataModalOpen && (
        <FileUploadModal
          open={isAddDataModalOpen}
          onClose={() => {
            setIsAddDataModalOpen(false);
            refetch();
          }}
          isAddData
          initFileType={
            projectBoard?.currentProjectData?.is_api_connected
              ? 'API'
              : getFileType(
                  projectBoard?.currentProjectData?.project_datafiles || []
                )
          }
          isCombineReady={isCombineReady}
          projectDataFiles={projectBoard?.currentProjectData?.project_datafiles}
          setIsCombineReady={setIsCombineReady}
          setInitiatePolling={setInitiateCombinePolling}
        />
      )}
      {openInsightsModal && (
        <InsightsModal
          open={openInsightsModal}
          onClose={() => setOpenInsightsModal(false)}
          insights={
            typeof projectBoard?.currentProjectData?.project_insights ===
              'string' &&
            projectBoard?.currentProjectData?.project_insights !== '' &&
            projectBoard.currentProjectData?.project_insights !==
              INSIGHT_CREATION_FAILED
              ? projectBoard?.currentProjectData?.project_insights
              : '[]'
          }
          projectId={projectBoard?.currentProjectData?.id || ''}
          currentDesc={
            projectBoard?.currentProjectData?.project_short_desc as string
          }
        />
      )}
      {openRefreshDataModal && (
        <RefreshDataModal
          sheetName={projectData?.project_name ?? ''}
          onClose={handleRefreshDataModalClose}
          lastUpdatedTimestamp={getUTCTime(
            projectBoard?.currentProjectData?.project_data_updated_at as string
          )}
          planName={userDetails.plan}
          handleRefreshClick={handleRefreshClick}
        />
      )}
      <Snackbar
        open={!!infoSnackBarMessage}
        autoHideDuration={3000}
        onClose={() => setInfoSnackBarMessage('')}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <Grid container sx={{ flex: 1, justifyContent: 'center' }}>
          <Alert
            severity='info'
            sx={{ display: 'flex', alignItems: 'top', marginTop: '10px' }}
            onClose={() => setInfoSnackBarMessage('')}
          >
            {infoSnackBarMessage}
          </Alert>
        </Grid>
      </Snackbar>
    </>
  );
};

export default AddChartNew;
